import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const RGSLedger = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [balanceData, setBalanceData] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [selectedDept, setSelectedDept] = useState(null);
  const [userData, setUserData] = useState({});
  const [error, setError] = useState(null);
  const [currentBalance, setCurrentBalance] = useState(0);

  // Search states
  const [departmentSearchTerm, setDepartmentSearchTerm] = useState('');
  const [transactionSearchTerm, setTransactionSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState({ from: '', to: '' });

  useEffect(() => {
    // Fetch balance data when component mounts
    fetchBalanceData();
  }, []);

  // Update current balance whenever transactions change
  useEffect(() => {
    if (transactions.length > 0) {
      // Get the latest balance from the first transaction (most recent)
      setCurrentBalance(parseFloat(transactions[0].balance));
    } else {
      setCurrentBalance(0);
    }
  }, [transactions]);

  // Function to get RGS scheme balance
  const getRGSBalance = (detail) => {
    if (!detail || !Array.isArray(detail)) return 0;
    
    try {
      const scheme = detail.find(s => {
        // Check if s exists and has a scheme property for RGS
        return s && typeof s === 'object' && s.scheme === 'RGS';
      });
      
      return scheme && scheme.balance ? parseFloat(scheme.balance) : 0;
    } catch (error) {
      console.error('Error in getRGSBalance:', error);
      return 0;
    }
  };

  // Function to fetch department balance data
  const fetchBalanceData = async () => {
    setLoading(true);
    try {
      const response = await api.ledger.getBalance();
      
      if (!response.error) {
        // Filter to only show departments that have RGS scheme data
        const filteredData = (response.data || []).filter(dept => {
          return dept && dept.detail && getRGSBalance(dept.detail) > 0;
        });
        setBalanceData(filteredData);
        setUserData(response.user || {});
      } else {
        setError(response.message || 'Failed to fetch balance data');
      }
    } catch (error) {
      console.error('Error fetching balance data:', error);
      setError('An error occurred while fetching balance data');
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch transaction data for a specific department (RGS only)
  const fetchDepartmentTransactions = async (deptCode) => {
    setLoading(true);
    try {
      const response = await api.ledger.getDepartmentTransactions(deptCode);
      
      if (!response.error) {
        setTransactions(response.data || []);
        setSelectedDept(deptCode);
        
        // Update current balance if there are transactions
        if (response.data && response.data.length > 0) {
          setCurrentBalance(parseFloat(response.data[response.data.length - 1].balance));
        }
      } else {
        setError(response.message || 'Failed to fetch transaction data');
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setError('An error occurred while fetching transaction data');
    } finally {
      setLoading(false);
    }
  };

  // Filter departments based on search term (only those with RGS)
  const filteredDepartments = balanceData.filter(dept => {
    if (!departmentSearchTerm) return true;
    
    const searchTerm = departmentSearchTerm.toLowerCase();
    return (
      (dept.type && dept.type.toLowerCase().includes(searchTerm)) ||
      (dept.name && dept.name.toLowerCase().includes(searchTerm)) ||
      (dept.center && dept.center.toLowerCase().includes(searchTerm))
    );
  });

  // Filter transactions based on search term and date range
  const filteredTransactions = transactions.filter(transaction => {
    // Text search filter
    if (transactionSearchTerm) {
      const searchTerm = transactionSearchTerm.toLowerCase();
      const matchesText = transaction.particulars.toLowerCase().includes(searchTerm);
      if (!matchesText) return false;
    }

    // Date range filter
    if (dateFilter.from || dateFilter.to) {
      const transactionDate = new Date(transaction.date);
      
      if (dateFilter.from) {
        const fromDate = new Date(dateFilter.from);
        if (transactionDate < fromDate) return false;
      }
      
      if (dateFilter.to) {
        const toDate = new Date(dateFilter.to);
        if (transactionDate > toDate) return false;
      }
    }

    return true;
  });

  // Clear all filters
  const clearFilters = () => {
    setDepartmentSearchTerm('');
    setTransactionSearchTerm('');
    setDateFilter({ from: '', to: '' });
  };

  // Format date to readable format
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-md">
        <p>{error}</p>
        <button 
          onClick={fetchBalanceData}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* RGS specific welcome header */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg shadow-sm mb-4">
        <h2 className="text-2xl font-bold text-gray-800">
          RGSA Scheme Ledger - Welcome, {userData.name}
        </h2>
  
      </div>

      {/* Department Balance List - RGS Only */}
      {!selectedDept && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-green-50">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <h3 className="text-lg font-medium text-gray-700">
                 Ledger Values for RGSA
              
              </h3>
              
              {/* Department Search */}
              <div className="flex items-center gap-2">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search departments..."
                    value={departmentSearchTerm}
                    onChange={(e) => setDepartmentSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                  <svg 
                    className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                
                {departmentSearchTerm && (
                  <button
                    onClick={() => setDepartmentSearchTerm('')}
                    className="px-3 py-2 text-sm bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>

          {filteredDepartments.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-green-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Center Code
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Center Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Center Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <span className="bg-green-100 text-green-700 px-2 py-1 rounded">RGSA Balance</span>
                    </th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredDepartments.map((dept, index) => (
                    <tr key={index} className="hover:bg-green-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-medium">
                        {dept.type ? dept.type.toUpperCase() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {dept.name || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {dept.center || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-600">
                        <span className="text-green-600 font-semibold text-lg">
                          {formatCurrency(getRGSBalance(dept.detail))}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                        <button
                          onClick={() => dept.type && fetchDepartmentTransactions(`${dept.type}-RGS`)}
                          className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500"
                        >
                          View Transactions
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-6 text-center text-gray-500">
              {departmentSearchTerm ? 
                `No departments found matching "${departmentSearchTerm}"` : 
                'No RGS scheme data found.'
              }
            </div>
          )}
        </div>
      )}

      {/* Transaction History */}
      {selectedDept && (
        <>
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-4 gap-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-800">
                {selectedDept.toUpperCase()}  Transactions
              </h2>
            
            </div>
            
            <div className="flex flex-col md:flex-row gap-4 w-full md:w-auto">
              <button
                onClick={() => {
                  setSelectedDept(null);
                  setTransactions([]);
                  clearFilters();
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-center w-full md:w-auto"
              >
                Back to RGS Departments
              </button>
            </div>
          </div>

          {/* Transaction Search and Filter */}
          <div className="bg-white rounded-lg shadow p-4 mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Search & Filter RGSA Transactions</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Text Search */}
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search particulars..."
                  value={transactionSearchTerm}
                  onChange={(e) => setTransactionSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
                <svg 
                  className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>

              {/* Date From */}
              <div>
                <label htmlFor="dateFrom" className="block text-xs text-gray-600 mb-1">From Date</label>
                <input
                  id="dateFrom"
                  type="date"
                  value={dateFilter.from}
                  onChange={(e) => setDateFilter(prev => ({ ...prev, from: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>

              {/* Date To */}
              <div>
                <label htmlFor="dateTo" className="block text-xs text-gray-600 mb-1">To Date</label>
                <input
                  id="dateTo"
                  type="date"
                  value={dateFilter.to}
                  onChange={(e) => setDateFilter(prev => ({ ...prev, to: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>

              {/* Clear Filters */}
              <div className="flex items-end">
                <button
                  onClick={clearFilters}
                  className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="p-4 border-b border-gray-200 bg-green-50">
              <h3 className="text-lg font-medium text-gray-700">
                RGSA Transaction History 
                {filteredTransactions.length !== transactions.length && (
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    ({filteredTransactions.length} of {transactions.length} transactions)
                  </span>
                )}
              </h3>
            </div>

            {filteredTransactions.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-green-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Particulars
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Balance
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredTransactions.map((transaction, index) => (
                      <tr key={index} className="hover:bg-green-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {formatDate(transaction.date)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {transaction.particulars}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                          {transaction.cr && transaction.cr !== "0.00" ? (
                            <span className="text-green-600 font-medium">
                              {formatCurrency(transaction.cr)}
                            </span>
                          ) : transaction.dr && transaction.dr !== "0.00" ? (
                            <span className="text-red-600 font-medium">
                              {formatCurrency(transaction.dr)}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium">
                          {formatCurrency(transaction.balance)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-6 text-center text-gray-500">
                {transactionSearchTerm || dateFilter.from || dateFilter.to ? 
                  'No RGSA transactions found matching the current filters.' : 
                  'No RGSA transaction records found.'
                }
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-700 mb-4">RGSA Summary</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-green-50 p-4 rounded-lg border border-green-100">
                <p className="text-sm text-green-800">As per PFMS</p>
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency(
                    filteredTransactions.reduce((sum, t) => sum + (parseFloat(t.cr) || 0), 0)
                  )}
                </p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-100">
                <p className="text-sm text-red-800">As per Administrative Sanctioned</p>
                <p className="text-2xl font-bold text-red-600">
                  {formatCurrency(
                    filteredTransactions.reduce((sum, t) => sum + (parseFloat(t.dr) || 0), 0)
                  )}
                </p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                <p className="text-sm text-blue-800">Current RGSA Balance</p>
                <p className="text-2xl font-bold text-blue-600">
                  {formatCurrency(currentBalance)}
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default RGSLedger;